import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Grid, Box } from '@mui/material';
import TablePagination from '@mui/material/TablePagination';
import Paper from '@mui/material/Paper';
import DelayedRow from '../../pages/ProjectDashboard/components/DelayedWorkRow';
import { Lables } from 'src/constants/labels';
import { tableStyles } from './styled';
import { Label, SearchInputWrapper } from 'src/shared';
import { ReactComponent as AppLogo } from 'src/assets/icons/small-app-logo.svg';
import {
    TableControlsContainer,
    TableEditContainer
} from 'src/pages/Community/CommunityContributors/styled';
import { HistoryText, labelStyle } from '../../pages/ProjectDashboard/styled';
import { IProjectData, IProjectDelayedTask } from 'src/types/projects';
import color from 'src/theme/color';

export interface Props {
    projectData: IProjectData;
}

const DelayedTask: FunctionComponent<Props> = ({ projectData }: Props) => {
    const [search, setSearch] = useState<string>('');
    const [page, setPage] = useState<number>(0);
    const [rowsPerPage, setRowsPerPageWorkDelay] = useState<number>(10);
    const delayedTasks = projectData?.delayedTasks;

    const filteredTasks = useMemo(() => {
        if (search) {
            const lowercaseQuery = search.toLowerCase();
            const filteredRows = (delayedTasks || []).filter(
                (row: IProjectDelayedTask) =>
                    row.taskName?.toLowerCase().includes(lowercaseQuery)
            );
            return filteredRows;
        } else {
            return delayedTasks;
        }
    }, [search, delayedTasks]);

    useEffect(() => {
        setPage(0);
    }, [search, delayedTasks]);

    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        const input = event.target.value;
        setSearch(input);
    };

    const handleChangePage = (
        _event: React.MouseEvent<HTMLButtonElement> | null,
        pageChanged: number
    ) => {
        setPage(pageChanged);
    };

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        setRowsPerPageWorkDelay(parseInt(event.target.value, 10));
        setPage(0);
    };

    const delayedTaskSlice = (filteredTasks || []).slice(
        page * rowsPerPage,
        (page + 1) * rowsPerPage
    );

    return (
        <Box marginTop={4}>
            <TableControlsContainer sx={tableStyles.tableControls}>
                <Grid
                    container
                    spacing={2}
                    columns={{ xs: 12, sm: 12, md: 12, lg: 12 }}
                >
                    <Grid
                        item
                        xs={12}
                        sm={12}
                        md={7}
                        lg={7}
                        sx={{
                            color: color.white,
                            display: {
                                xs: 'block',
                                sm: 'block',
                                md: 'block',
                                lg: 'block'
                            }
                        }}
                    >
                        <Box sx={{ mb: 1 }}>
                            <Label sxOverrides={HistoryText}>
                                {Lables.delayedWorkTitle}
                            </Label>
                        </Box>
                        <Box>
                            <Label sxOverrides={labelStyle}>
                                {Lables.delayedWorkDesc}
                            </Label>
                        </Box>
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        sm={12}
                        md={5}
                        lg={5}
                        sx={{
                            display: 'flex',
                            alignItems: {
                                xs: 'flex-end',
                                sm: 'flex-end',
                                md: 'flex-end'
                            }
                        }}
                    >
                        <TableEditContainer>
                            <SearchInputWrapper
                                search={search}
                                handleSearch={handleSearch}
                                sx={{
                                    width: {
                                        xs: '300px',
                                        sm: '300px',
                                        md: '500px',
                                        lg: '500px'
                                    }
                                }}
                            />
                        </TableEditContainer>
                    </Grid>
                </Grid>
            </TableControlsContainer>
            <TableContainer
                component={Paper}
                sx={{
                    ...tableStyles.container,
                    overflow: 'auto'
                }}
            >
                <Table
                    stickyHeader
                    sx={tableStyles.table}
                    aria-label="simple table"
                >
                    <TableHead>
                        <TableRow sx={tableStyles.dataRow}>
                            <TableCell
                                sx={{
                                    ...tableStyles.muiTableCell,
                                    ...tableStyles.headCell,
                                    ...tableStyles.rowView,
                                    ...tableStyles.pointer,
                                    position: 'sticky',
                                    top: 0,
                                    zIndex: 10
                                }}
                            >
                                <Label
                                    component="span"
                                    sxOverrides={tableStyles.baseLabel}
                                >
                                    Time created
                                </Label>
                            </TableCell>
                            <TableCell
                                sx={{
                                    ...tableStyles.muiTableCell,
                                    ...tableStyles.headCell,
                                    ...tableStyles.rowView,
                                    position: 'sticky',
                                    top: 0,
                                    zIndex: 10
                                }}
                            >
                                <Label
                                    component="span"
                                    sxOverrides={tableStyles.baseLabel}
                                >
                                    Task name
                                </Label>
                            </TableCell>
                            <TableCell
                                sx={{
                                    ...tableStyles.muiTableCell,
                                    ...tableStyles.headCell,
                                    ...tableStyles.rowView,
                                    position: 'sticky',
                                    top: 0,
                                    zIndex: 10
                                }}
                            >
                                <Label
                                    component="span"
                                    sxOverrides={tableStyles.baseLabel}
                                >
                                    Task ID
                                </Label>
                                <Box sx={{ width: '5px' }} />
                                <AppLogo />
                            </TableCell>
                            <TableCell
                                sx={{
                                    ...tableStyles.muiTableCell,
                                    ...tableStyles.headCell,
                                    ...tableStyles.rowView,
                                    ...tableStyles.pointer,
                                    position: 'sticky',
                                    top: 0,
                                    zIndex: 10
                                }}
                            >
                                <Label
                                    component="span"
                                    sxOverrides={tableStyles.baseLabel}
                                >
                                    Skill type
                                </Label>
                            </TableCell>
                            <TableCell
                                sx={{
                                    ...tableStyles.muiTableCell,
                                    ...tableStyles.headCell,
                                    ...tableStyles.rowView,
                                    ...tableStyles.pointer,
                                    position: 'sticky',
                                    top: 0,
                                    zIndex: 10
                                }}
                            >
                                <Label
                                    component="span"
                                    sxOverrides={tableStyles.baseLabel}
                                >
                                    Task status
                                </Label>
                            </TableCell>
                            <TableCell
                                sx={{
                                    ...tableStyles.muiTableCell,
                                    ...tableStyles.headCell,
                                    ...tableStyles.rowView,
                                    ...tableStyles.pointer,
                                    position: 'sticky',
                                    top: 0,
                                    zIndex: 10
                                }}
                            >
                                <Label
                                    component="span"
                                    sxOverrides={tableStyles.baseLabel}
                                >
                                    Billable time
                                </Label>
                            </TableCell>
                            <TableCell
                                sx={{
                                    ...tableStyles.muiTableCell,
                                    ...tableStyles.headCell,
                                    ...tableStyles.rowView,
                                    ...tableStyles.pointer,
                                    position: 'sticky',
                                    top: 0,
                                    zIndex: 10
                                }}
                            >
                                <Label
                                    component="span"
                                    sxOverrides={tableStyles.baseLabel}
                                >
                                    Last updated
                                </Label>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody style={tableStyles.body}>
                        {delayedTaskSlice.map((row, i) => (
                            <DelayedRow row={row} key={i} />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                sx={{
                    ...tableStyles.pagination,
                    ...tableStyles.paginationLabel,
                    ' svg': tableStyles.caret,
                    '& .MuiTablePagination-select': tableStyles.paginationSelect
                }}
                count={(filteredTasks || []).length}
                page={page}
                rowsPerPageOptions={[5, 10, 20, 50, 100]}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </Box>
    );
};

export default DelayedTask;
